import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Add, EventAvailable, Remove } from "@mui/icons-material";
import DialogHeader from "./DialogHeader";

export interface ConfirmSessionBookingDetails {
  name: string;
  dateLabel: string;
  fee: number;
  notes?: string | null;
  maxPlaces: number;
}

interface ConfirmSessionBookingDialogProps {
  open: boolean;
  details: ConfirmSessionBookingDetails | null;
  confirming?: boolean;
  onConfirm: (quantity: number) => void;
  onClose: () => void;
}

const ConfirmSessionBookingDialog: React.FC<ConfirmSessionBookingDialogProps> = ({
  open,
  details,
  confirming = false,
  onConfirm,
  onClose,
}) => {
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (open) {
      setQuantity(1);
    }
  }, [open, details?.name, details?.dateLabel]);

  const maxPlaces = Math.max(1, details?.maxPlaces ?? 1);
  const fee = details?.fee ?? 0;
  const isFree = details != null && fee <= 0;
  const total = fee * quantity;

  const dec = () => setQuantity((q) => Math.max(1, q - 1));
  const inc = () => setQuantity((q) => Math.min(maxPlaces, q + 1));

  return (
    <Dialog open={open} onClose={confirming ? () => {} : onClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title="Confirm your booking"
        icon={<EventAvailable color="primary" />}
        onClose={onClose}
        closeDisabled={confirming}
      />
      <DialogContent dividers>
        {details && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {details.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {details.dateLabel}
              {" · "}
              {isFree ? "Free per place" : `£${fee.toFixed(2)} per place`}
            </Typography>

            {details.notes?.trim() && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Session notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {details.notes.trim()}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Number of places
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton
                onClick={dec}
                disabled={confirming || quantity <= 1}
                size="small"
                aria-label="Fewer places"
              >
                <Remove />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 32, textAlign: "center" }}>
                {quantity}
              </Typography>
              <IconButton
                onClick={inc}
                disabled={confirming || quantity >= maxPlaces}
                size="small"
                aria-label="More places"
              >
                <Add />
              </IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {maxPlaces === 1 ? "1 place available" : `Up to ${maxPlaces} places`}
              </Typography>
            </Box>

            {!isFree && (
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                Total: £{total.toFixed(2)}
              </Typography>
            )}

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              When you confirm:
            </Typography>
            <List dense disablePadding sx={{ mb: 2 }}>
              <ListItem sx={{ px: 0, py: 0.25 }}>
                <ListItemText
                  primary={
                    quantity === 1
                      ? "Your place on this session will be reserved for you."
                      : `${quantity} places on this session will be reserved for you.`
                  }
                />
              </ListItem>
              {!isFree && (
                <>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemText primary="One invoice will be created straight away for the total amount." />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemText primary="You will see bank transfer details and a payment reference (INV-…) to pay the club." />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemText primary="If payment is not received within 24 hours, your booking will be cancelled and the places released." />
                  </ListItem>
                </>
              )}
              {isFree && (
                <ListItem sx={{ px: 0, py: 0.25 }}>
                  <ListItemText primary="No payment is required for this session." />
                </ListItem>
              )}
            </List>

            {!isFree && (
              <Alert severity="info">
                By confirming, you agree to pay the session fee using the reference
                on your invoice.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(quantity)}
          disabled={confirming || maxPlaces < 1}
        >
          {confirming ? "Booking…" : "Confirm booking"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmSessionBookingDialog;
